<template>
  <EdroneDrawer
    :class="$options.name"
    :open="!state.hasTag('closed')"
    :pinned="canBePinned && pinned"
    :without-backdrop="withoutBackdrop"
    class="tw:border-l tw:border-default-200 tw:max-w-[35rem]"
    unique-id="aa04ad0d-d6cd-4081-ae44-1cc3de983e8d"
    width="35rem"
    @close="handleClose"
  >
    <div class="tw:h-full tw:flex tw:flex-col">
      <div class="tw:pt-3 tw:px-6 tw:border-b tw:border-default-200 tw:bg-default-000 tw:relative">
        <div class="tw:px-6 tw:flex tw:w-full tw:justify-between tw:absolute tw:top-3 tw:left-0 tw:right-0">
          <EdroneButtonIcon
            v-if="canBePinned"
            size="medium"
            type="plain"
            unique-id="cad6afe7-d96c-453c-9e93-7c89eb68eaf5"
            @click="pinned = !pinned"
          >
            <font-awesome-icon
              :class="!pinned ? 'tw:rotate-45' : 'tw:rotate-0'"
              class="tw:transition-transform tw:duration-300 tw:ease-in-out"
              icon="fa-solid fa-thumbtack"
            />
          </EdroneButtonIcon>
        </div>
        <div class="tw:flex tw:items-center tw:pt-4 tw:gap-2 flex-column">
          <EdroneAvatar
            :customer-id="customer && customer.customerId ? customer.customerId : 4"
            :loading="state.hasTag('loadingCustomer')"
            size="big"
            unique-id="9a6bf805-25f4-44bf-baf1-89b7a09c9e35"
          />
          <h2
            v-if="
              !(customer && customer.customerId) ||
              (!customer.firstName && !customer.lastName) ||
              state.hasTag('loadingCustomer')
            "
            class="tw:heading-2 tw:flex tw:items-center"
            style="height: 32px; color: #bfbfbf"
          >
            {{ trans._inbox.customer.header.name }}
          </h2>
          <h2 v-else class="tw:heading-2 tw:flex tw:items-center" style="height: 32px">
            {{ customer?.firstName }} {{ customer?.lastName }}
          </h2>
          <div class="tw:flex tw:gap-1">
            <EdroneBadge
              :disabled="!(customer && customer.exist) || state.hasTag('loadingCustomer')"
              :tooltip="emailStatusBadgeTooltip"
              :type="getSubscriptionStatusType('emailStatus')"
              label="Email"
              unique-id="3e9d1142-d9dd-41f6-aa37-5b2ec3d0ebd7"
              size="small"
            />
            <EdroneBadge
              :disabled="!(customer && customer.exist) || state.hasTag('loadingCustomer')"
              :tooltip="smsStatusBadgeTooltip"
              :type="getSubscriptionStatusType('smsStatus')"
              label="SMS"
              unique-id="46dcc80b-25ef-43b9-b16b-96d8c8eb8d12"
              size="small"
            />
            <EdroneBadge
              :disabled="!(customer && customer.exist) || state.hasTag('loadingCustomer')"
              :tooltip="whatsappStatusBadgeTooltip"
              :type="getSubscriptionStatusType('whatsappStatus')"
              label="WhatsApp"
              unique-id="46dcc80b-25ef-43b9-b16b-96d8c8eb8d13"
              size="small"
            />
          </div>
          <div class="tw:flex tw:gap-2 tw:my-2">
            <CustomerMessage
              v-if="isInboxPage && !skipInboxForSearchbar"
              :customer-id="customer?.customerId || 0"
              :email="customer?.email || ''"
              :has-close-tag="hasCloseTag"
              :is-pinned="pinned"
              :is-valid-email="isValidEmail"
              :phone="customer?.phone || ''"
              :send="send"
              @close="handleClose"
              @update-customer="(customerId) => send({ type: 'UPDATE_CUSTOMER', customerId })"
            />
            <EdroneButtonIcon
              :disabled="!state.can('EXPORT_TO_HTML')"
              :tooltip="trans._inbox.customer.footer.buttons.export"
              type="plain"
              unique-id="7f73e1a6-7dea-4bb1-9e9b-455b5fbea894"
              @click="send('EXPORT_TO_HTML')"
            >
              <font-awesome-icon icon="fa-light fa-arrow-down-to-line" />
            </EdroneButtonIcon>
            <EdroneButtonIcon
              v-if="isDev"
              :disabled="!state.can('CLEAR_TPCID')"
              :tooltip="trans._inbox.customer.footer.buttons.clear"
              type="plain"
              unique-id="327d39f4-f284-4608-abf3-ad7d067a876b"
              @click="send('CLEAR_TPCID')"
            >
              <font-awesome-icon icon="fa-light fa-broom-wide" />
            </EdroneButtonIcon>
            <EdroneButtonIcon
              :disabled="!state.can({ type: 'OPEN_CONFIRMATION_MODAL', confirmation_type: 'unsubscribe' })"
              :tooltip="trans._inbox.customer.footer.buttons.unsubscribe"
              type="plain"
              unique-id="4cf19f00-2610-4dda-97fe-e11e24d83d8e"
              @click="send({ type: 'OPEN_CONFIRMATION_MODAL', confirmation_type: 'unsubscribe' })"
            >
              <font-awesome-icon icon="fa-light fa-user-xmark" />
            </EdroneButtonIcon>
            <EdroneButtonIcon
              :disabled="!state.can({ type: 'OPEN_CONFIRMATION_MODAL', confirmation_type: 'delete' })"
              :tooltip="trans._inbox.customer.footer.buttons.delete"
              type="plain"
              unique-id="e9b49204-190b-4213-9659-2afdb984057c"
              @click="send({ type: 'OPEN_CONFIRMATION_MODAL', confirmation_type: 'delete' })"
            >
              <font-awesome-icon icon="fa-light fa-trash-can" />
            </EdroneButtonIcon>
          </div>
        </div>
        <div class="tw:flex tw:justify-center">
          <EdroneSelectCustom
            v-if="isMobile"
            :allow-empty="false"
            :options="tabOptions"
            :selected-options="selectedTabOption"
            class="tw:w-full tw:mx-6 tw:mb-2"
            @select="send({ type: 'SELECT_TAB', tab: $event.id })"
          />
          <EdroneTabGroup v-else type="underline" unique-id="ad1dedcc-ea46-46cd-8851-01c7b04ee3fe">
            <EdroneTab
              :active="selectedTab === tabs.properties"
              :disabled="!state.can({ type: 'SELECT_TAB', tab: tabs.properties })"
              type="underline"
              unique-id="ad04459e-3a1b-4566-b56a-963355cf49f6"
              @click="send({ type: 'SELECT_TAB', tab: tabs.properties })"
            >
              {{ trans._inbox.customer.tabs.properties }}
            </EdroneTab>
            <EdroneTab
              :active="selectedTab === tabs.activities"
              :disabled="!state.can({ type: 'SELECT_TAB', tab: tabs.activities })"
              type="underline"
              unique-id="d5b3dfcf-42c4-4ab1-8b1e-ac00643f1b3e"
              @click="send({ type: 'SELECT_TAB', tab: tabs.activities })"
            >
              {{ trans._inbox.customer.tabs.activity }}
            </EdroneTab>
            <EdroneTab
              :active="selectedTab === tabs.tags"
              :disabled="!state.can({ type: 'SELECT_TAB', tab: tabs.tags })"
              type="underline"
              unique-id="f738cf6c-8fbb-44cb-aec7-6d88857468c5"
              @click="send({ type: 'SELECT_TAB', tab: tabs.tags })"
            >
              {{ trans._inbox.customer.tabs.tags }}
            </EdroneTab>
            <EdroneTab
              :active="selectedTab === tabs.system"
              :disabled="!state.can({ type: 'SELECT_TAB', tab: tabs.system })"
              type="underline"
              unique-id="79890f73-e648-4a88-ab2c-c2c7cb274ead"
              @click="send({ type: 'SELECT_TAB', tab: tabs.system })"
            >
              {{ trans._inbox.customer.tabs.system }}
            </EdroneTab>
          </EdroneTabGroup>
        </div>
      </div>

      <div class="tw:flex-1 tw:min-h-0 tw:overflow-y-auto">
        <div
          v-if="state.hasTag('loadingCustomer') || state.hasTag('deletingCustomer')"
          class="tw:p-6 tw:h-full tw:grid tw:place-items-center"
        >
          <EdroneResultState type="loading" unique-id="9bd45023-8f87-45d6-a15a-1f9f27853dde" />
        </div>
        <div
          v-else-if="state.hasTag('errorGettingCustomer') || !customer"
          class="tw:p-6 tw:h-full tw:grid tw:place-items-center"
        >
          <EdroneResultState type="error" unique-id="d13f2193-bb67-409c-a6d2-d10448711296">
            <h3 class="tw:heading-2 tw:mb-1">{{ trans._inbox.customer.results.error.title }}</h3>
            <p class="tw:body-2 tw:mb-4">{{ trans._inbox.customer.results.error.description }}</p>
            <div>
              <EdroneButton
                type="outline"
                unique-id="e2a93ee6-faf0-483e-8bc4-b0bdc4010f74"
                @click="send({ type: 'RETRY_CUSTOMER' })"
              >
                {{ trans._inbox.customer.results.error.retry }}
              </EdroneButton>
            </div>
          </EdroneResultState>
        </div>
        <div v-else-if="state.hasTag('emptyCustomer')" class="tw:p-6 tw:h-full tw:grid tw:place-items-center">
          <EdroneResultState type="empty" unique-id="3621de98-4127-4893-a751-7d6f94857c60">
            <template #top-icon>
              <font-awesome-icon icon="fa-light fa-user-slash" />
            </template>
            <h3 class="tw:heading-2 tw:mt-4 tw:mb-1">{{ trans._inbox.customer.results.empty.title }}</h3>
            <p class="tw:body-2">{{ trans._inbox.customer.results.empty.description }}</p>
          </EdroneResultState>
        </div>
        <div v-else>
          <template v-if="selectedTab === tabs.properties">
            <EdroneLoading
              :is-loading="state.hasTag('savingCustomer')"
              unique-id="5feefcd9-4dd3-4347-b293-b9753a171f44"
            >
              <CustomerPreviewProperties />
            </EdroneLoading>
          </template>
          <template v-else-if="selectedTab === tabs.activities">
            <CustomerPreviewActivity class="tw:p-6" style="padding-top: 20px !important" />
          </template>
          <template v-else-if="selectedTab === tabs.tags">
            <EdroneLoading
              :is-loading="state.hasTag('savingCustomer') || state.hasTag('creatingTag')"
              unique-id="d275262b-9a58-4189-8f03-8363e0aa72a8"
            >
              <CustomerPreviewTags class="tw:p-6" />
            </EdroneLoading>
          </template>
          <template v-else-if="selectedTab === tabs.system">
            <CustomerPreviewSystem class="tw:p-6" />
          </template>
        </div>
      </div>

      <EdroneNavigationBottom>
        <template #start>
          <EdroneButton
            :disabled="!state.can('CLOSE')"
            type="text"
            unique-id="48320708-5b0c-4f06-90a0-450cc4b14c7f"
            @click="handleClose"
          >
            {{ trans._inbox.customer.footer.buttons.close }}
          </EdroneButton>
        </template>
        <template #end>
          <EdroneButton
            :disabled="!state.can('SAVE')"
            type="solid"
            unique-id="320d1b4c-374c-49ad-b2b4-0ade228133e3"
            @click="send('SAVE')"
          >
            {{ trans._inbox.customer.footer.buttons.save }}
          </EdroneButton>
        </template>
      </EdroneNavigationBottom>
    </div>

    <EdroneModal
      :open="state.hasTag('confirmationModal')"
      unique-id="8818b00b-0cf7-4afb-a7e0-46a479784523"
      @close="send('CLOSE_CONFIRMATION_MODAL')"
    >
      <template #title>
        {{
          state.context.confirmationModalType === "unsubscribe"
            ? trans._inbox.customer.confirmations.unsubscribe.title
            : trans._inbox.customer.confirmations.delete.title
        }}
      </template>
      <p class="tw:body-2 tw:mb-4 tw:text-default-700">
        {{
          state.context.confirmationModalType === "unsubscribe"
            ? trans._inbox.customer.confirmations.unsubscribe.description
            : trans._inbox.customer.confirmations.delete.description
        }}
      </p>
      <div class="tw:flex tw:gap-2">
        <EdroneButton
          class="tw:flex-1"
          type="outline"
          unique-id="d4361cea-69d7-42b0-ae6a-6ca844d573f4"
          @click="send('CLOSE_CONFIRMATION_MODAL')"
        >
          {{ trans._inbox.customer.properties.subscriptions.confirmation.cancel }}
        </EdroneButton>
        <EdroneButton
          v-if="state.context.confirmationModalType === 'unsubscribe'"
          :disabled="!state.can('UNSUBSCRIBE_ALL')"
          class="tw:flex-1"
          type="danger"
          unique-id="d729444b-d806-483b-a5ab-0be370383a4b"
          @click="
            send('UNSUBSCRIBE_ALL');
            send('CLOSE_CONFIRMATION_MODAL');
          "
        >
          {{ trans._inbox.customer.confirmations.unsubscribe.confirm }}
        </EdroneButton>
        <EdroneButton
          v-if="state.context.confirmationModalType === 'delete'"
          :disabled="!state.can('DELETE_CUSTOMER')"
          class="tw:flex-1"
          type="danger"
          unique-id="83ee7787-3755-46a7-a5a8-cbdf7d6e1926"
          @click="
            send('DELETE_CUSTOMER');
            send('CLOSE_CONFIRMATION_MODAL');
          "
        >
          {{ trans._inbox.customer.confirmations.delete.confirm }}
        </EdroneButton>
      </div>
    </EdroneModal>
  </EdroneDrawer>
</template>

<script lang="ts">
import { computed, defineComponent, ref, watch } from "vue";
import CustomerPreviewProperties from "vue-app/components/Customer/CustomerPreviewProperties.vue";
import CustomerPreviewTags from "vue-app/components/Customer/CustomerPreviewTags.vue";
import CustomerPreviewSystem from "vue-app/components/Customer/CustomerPreviewSystem.vue";
import CustomerPreviewActivity from "vue-app/components/Customer/CustomerPreviewActivity.vue";
import { useCustomerPreviewActor } from "../../pages/machines/machineCustomerPreview";
import useDeviceResolutionDetector from "vue-app/composables/useDeviceResolutionDetector.ts";
import EdroneModal from "edrone-components/EdroneModal.vue";
import EdroneDrawer from "edrone-components/EdroneDrawer.vue";
import EdroneBadge from "edrone-components/EdroneBadge/EdroneBadge.vue";
import EdroneTabGroup from "edrone-components/EdroneTabGroup.vue";
import EdroneTab from "edrone-components/EdroneTab.vue";
import EdroneButton from "edrone-components/EdroneButton.vue";
import EdroneButtonIcon from "edrone-components/EdroneButtonIcon.vue";
import EdroneResultState from "edrone-components/EdroneResultState.vue";
import EdroneLoading from "edrone-components/EdroneLoading.vue";
import EdroneAvatar from "edrone-components/EdroneAvatar.vue";
import EdroneNavigationBottom from "edrone-components/EdroneNavigationBottom.vue";
import EdroneSelectCustom from "edrone-components/EdroneSelectCustom.vue";
import { useRoute } from "vue-router";
import CustomerMessage from "./CustomerMessage.vue";

enum tabs {
  properties = "properties",
  tags = "tags",
  activities = "activities",
  system = "system",
}

export default defineComponent({
  name: "CustomerPreview",
  components: {
    EdroneModal,
    EdroneDrawer,
    EdroneBadge,
    EdroneTabGroup,
    EdroneTab,
    EdroneButton,
    EdroneButtonIcon,
    EdroneResultState,
    EdroneLoading,
    CustomerPreviewProperties,
    CustomerPreviewTags,
    CustomerPreviewSystem,
    CustomerPreviewActivity,
    EdroneAvatar,
    EdroneNavigationBottom,
    EdroneSelectCustom,
    CustomerMessage,
  },
  props: {
    skipInboxForSearchbar: {
      type: Boolean,
      required: false,
    },
    withoutBackdrop: {
      type: Boolean,
      required: false,
      default: false,
    },
  },
  emits: ["close"],
  setup(props, { emit }) {
    const { state, send } = useCustomerPreviewActor();
    const isDev = window.EDRONE_CONTEXT.user.isDev;
    const trackerId = window.EDRONE_CONTEXT.trackerId;
    const agentId = window.EDRONE_CONTEXT.user.agentId;
    const route = useRoute();
    const isInboxPage = route.path.includes("/app/inbox") || route.meta?.routeName === "Inbox";
    const pinned = ref(localStorage.getItem(`${trackerId}-${agentId}-customer-preview-pin`) === "true");
    const isMenuPinned = localStorage.getItem(`${trackerId}-${agentId}-customer-preview-pin`) === "true";
    // 1620 is the width of the drawer + the width of the chat box and conversation list
    const canBePinned = ref(window.innerWidth > (isMenuPinned ? 1620 + 230 : 1620) && isInboxPage);

    const { isMobile } = useDeviceResolutionDetector(true);

    watch(
      () => pinned.value,
      (pinned) => {
        localStorage.setItem(`${trackerId}-${agentId}-customer-preview-pin`, pinned.toString());
      }
    );

    const selectedTab = computed(() => state.value.context.selectedTab);
    const customer = computed(() => state.value.context.customer);

    const getSubscriptionStatusType = (type: "smsStatus" | "emailStatus" | "whatsappStatus") => {
      if (state.value.hasTag("loadingCustomer")) {
        return "default";
      }
      if (customer.value?.subscription && customer.value.subscription[type]?.toLowerCase() === "subscribed") {
        return "success";
      } else if (customer.value?.subscription && customer.value.subscription[type]?.toLowerCase() === "unsubscribed") {
        return "error";
      } else {
        return "default";
      }
    };

    const validCustomerEmail = (email: string | null | undefined) => {
      if (!email) {
        return false;
      }
      return !(
        email.toLowerCase().includes("chatcustomer") ||
        email.includes("customer-facebook-messenger") ||
        email.includes("whatsapp.edrone.me") ||
        email.includes("phone.edrone.me")
      );
    };

    const isValidEmail = computed(() => {
      return validCustomerEmail(customer.value?.email);
    });

    const emailStatusBadgeTooltip = computed(() => {
      return customer.value && customer.value.exist
        ? window._trans._inbox.customer.properties.subscriptions.status[
            customer.value.subscription.emailStatus.toLowerCase() as "subscribed" | "unsubscribed" | "unknown"
          ]
        : "";
    });

    const smsStatusBadgeTooltip = computed(() => {
      return customer.value && customer.value.exist
        ? window._trans._inbox.customer.properties.subscriptions.status[
            customer.value.subscription.smsStatus.toLowerCase() as "subscribed" | "unsubscribed" | "unknown"
          ]
        : "";
    });

    const whatsappStatusBadgeTooltip = computed(() => {
      return customer.value && customer.value.exist
        ? window._trans._inbox.customer.properties.subscriptions.status[
            customer.value.subscription.whatsappStatus.toLowerCase() as "subscribed" | "unsubscribed" | "unknown"
          ]
        : "";
    });

    const hasCloseTag = state.value.hasTag("closed");

    const handleClose = () => {
      send("CLOSE");
      emit("close");
    };

    const tabOptions = computed(() => {
      return [
        { id: tabs.properties, name: window._trans._inbox.customer.tabs.properties },
        { id: tabs.activities, name: window._trans._inbox.customer.tabs.activity },
        { id: tabs.tags, name: window._trans._inbox.customer.tabs.tags },
        { id: tabs.system, name: window._trans._inbox.customer.tabs.system },
      ];
    });

    const selectedTabOption = computed(() => {
      return tabOptions.value.find((tab) => tab.id === selectedTab.value) || null;
    });

    return {
      state,
      send,
      selectedTab,
      tabs,
      customer,
      getSubscriptionStatusType,
      isDev,
      pinned,
      canBePinned,
      emailStatusBadgeTooltip,
      smsStatusBadgeTooltip,
      whatsappStatusBadgeTooltip,
      isValidEmail,
      hasCloseTag,
      isInboxPage,
      handleClose,
      isMobile,
      tabOptions,
      selectedTabOption,
    };
  },
});
</script>
